import { useState } from "react";
import { motion } from "framer-motion";
import {
  CHARACTERS_MAP,
  MUSIC_MAP,
  CRAFTS_MAP,
  QUOTES_MAP,
  TRIVIA_MAP,
  LOCATIONS_MAP,
  SILHOUETTES_MAP,
} from "../maps";
import { CHARACTERS, QUOTES, MUSIC, LOCATIONS, TRIVIA, CRAFTS, SILHOUETTES } from "../data";
import { getDailyItem } from "../utils/gameLogic";

/**
 * Daily results dashboard showing won/lost/in-progress status and streaks for all 7 game modes.
 * Supports sharing results as emoji or text via clipboard, and toggling global community stats.
 *
 * @param {{ version: string, targetDateObj: Date, targetDateStr: string, isArchive: boolean, setIsModalOpen: (v: boolean) => void, currentTheme: string }} props
 * @param {string} props.version - "old" or "new" — controls display of newer modes.
 * @param {Date} props.targetDateObj - The date object for the results being viewed.
 * @param {string} props.targetDateStr - Date string key for localStorage lookups.
 * @param {boolean} props.isArchive - Whether this is an archived (past) date.
 * @param {(v: boolean) => void} props.setIsModalOpen - Opens the global stats modal.
 * @param {string} props.currentTheme - Active era theme name.
 * @returns {JSX.Element} The results page.
 */
function ResultsMode({ version, targetDateObj, targetDateStr, isArchive, setIsModalOpen, currentTheme }) {
  const [isCopied, setIsCopied] = useState(null);

  /**
   * Retrieves stored game data (guesses array + hasWon flag) for a given mode and date.
   * Includes a migration fallback for players mid-game on the old key format.
   *
   * @param {string} baseKey - Base localStorage key (e.g. "kisekidle-chars").
   * @returns {{ guesses: Array }} Parsed game record.
   */
  const getStoredData = (baseKey) => {
    // ALWAYS fetch the date-specific key
    const storageKey = `${baseKey}-${targetDateStr}`;
    const storedData = localStorage.getItem(storageKey);

    if (storedData) {
      try {
        const parsed = JSON.parse(storedData);
        // Robustness: ensure we always return an object with a guesses array
        if (Array.isArray(parsed)) return { guesses: parsed };
        if (parsed && !parsed.guesses) return { ...parsed, guesses: [] };
        return parsed || { guesses: [] };
      } catch (e) {
        return { guesses: [] };
      }
    }

    // MIGRATION FALLBACK (For players mid-game today)
    const todayStr = `${new Date().getUTCFullYear()}-${new Date().getUTCMonth() + 1
      }-${new Date().getUTCDate()}`;
    if (targetDateStr === todayStr) {
      const oldLegacyData = localStorage.getItem(baseKey);
      if (oldLegacyData) {
        try {
          const parsedOld = JSON.parse(oldLegacyData);
          if (parsedOld.date === targetDateStr) return parsedOld;
        } catch (e) { }
      }
    }

    return { guesses: [] };
  };

  const charData = getStoredData("kisekidle-chars");
  const quoteData = getStoredData("kisekidle-quotes");
  const musicData = getStoredData("kisekidle-music");
  const locData = getStoredData("kisekidle-locations");
  const triviaData = getStoredData("kisekidle-trivia");
  const craftData = getStoredData("kisekidle-crafts");
  const silhouetteData = getStoredData("kisekidle-silhouette");

  const targetChar = getDailyItem(CHARACTERS, targetDateObj, CHARACTERS_MAP);
  const targetQuote = getDailyItem(QUOTES, targetDateObj, QUOTES_MAP);
  const targetMusic = getDailyItem(MUSIC, targetDateObj, MUSIC_MAP);
  const targetLocation = getDailyItem(LOCATIONS, targetDateObj, LOCATIONS_MAP);
  const targetTrivia = getDailyItem(TRIVIA, targetDateObj, TRIVIA_MAP);
  const targetCraft = getDailyItem(CRAFTS, targetDateObj, CRAFTS_MAP);
  const targetSilhouette = getDailyItem(
    SILHOUETTES,
    targetDateObj,
    SILHOUETTES_MAP
  );

  /** @returns {boolean} Whether the guess matches the daily character. */
  const checkCharWin = (g, t) => g.id === t.id;
  /** @returns {boolean} Whether the guess matches the daily quote's speaker. */
  const checkQuoteWin = (g, t) => g.name === t.character;
  /** @returns {boolean} Whether the guess matches the daily music track. */
  const checkMusicWin = (g, t) => g.title === t.title;
  /** @returns {boolean} Whether the guess matches the daily location. */
  const checkLocWin = (g, t) => g.id === t.id;
  /** @returns {boolean} Whether the guess matches the daily craft (by name or craftName). */
  const checkCraftWin = (g, t) =>
    g.character === t.character || (g.name || g.craftName) === (t.name || t.craftName);
  /** @returns {boolean} Whether the guess matches the daily silhouette's character name. */
  const checkSilhWin = (g, t) => g === t.character;

  /**
   * @param {string} guess
   * @param {Object} [target=targetTrivia]
   * @returns {boolean} Whether the guess matches the answer or any acceptedAnswers alias.
   */
  const checkTriviaWin = (guess, target = targetTrivia) => {
    if (!target) return false;
    const guessLower = guess.toLowerCase().trim();
    const answerLower = target.answer.toLowerCase().trim();
    if (guessLower === answerLower) return true;
    if (target.acceptedAnswers) {
      return target.acceptedAnswers.some(
        (alt) => alt.toLowerCase().trim() === guessLower
      );
    }
    return false;
  };

  /**
   * Walks backward from the target date counting consecutive won days for a mode.
   * Stops on the first unplayed or lost day.
   *
   * @param {string} baseKey - localStorage key prefix.
   * @param {Array} dataArray - Full dataset for this mode.
   * @param {Array<number>} mapArray - Shuffled index map.
   * @param {(guess: Object, target: Object) => boolean} winCheckFn - Per-mode win check.
   * @returns {number} Current streak length.
   */
  const calculateStreak = (baseKey, dataArray, mapArray, winCheckFn) => {
    let streak = 0;
    let checkDate = new Date(targetDateObj.getTime());

    while (true) {
      const dateStr = `${checkDate.getUTCFullYear()}-${checkDate.getUTCMonth() + 1
        }-${checkDate.getUTCDate()}`;
      let guesses = [];
      const storedData = localStorage.getItem(`${baseKey}-${dateStr}`);

      if (storedData) {
        try {
          guesses = JSON.parse(storedData).guesses || [];
        } catch (e) { }
      }

      if (guesses.length === 0) break; // Unplayed day stops the streak

      const pastTargetItem = getDailyItem(dataArray, checkDate, mapArray);
      const hasWon = guesses.some((g) => winCheckFn(g, pastTargetItem));

      if (hasWon) {
        streak++;
        checkDate.setUTCDate(checkDate.getUTCDate() - 1);
      } else {
        break;
      }
    }
    return streak;
  };

  const charStreak = calculateStreak(
    "kisekidle-chars",
    CHARACTERS,
    CHARACTERS_MAP,
    checkCharWin
  );
  const quoteStreak = calculateStreak(
    "kisekidle-quotes",
    QUOTES,
    QUOTES_MAP,
    checkQuoteWin
  );
  const musicStreak = calculateStreak(
    "kisekidle-music",
    MUSIC,
    MUSIC_MAP,
    checkMusicWin
  );
  const locStreak = calculateStreak(
    "kisekidle-locations",
    LOCATIONS,
    LOCATIONS_MAP,
    checkLocWin
  );
  const triviaStreak = calculateStreak(
    "kisekidle-trivia",
    TRIVIA,
    TRIVIA_MAP,
    checkTriviaWin
  );
  const craftStreak = calculateStreak(
    "kisekidle-crafts",
    CRAFTS,
    CRAFTS_MAP,
    checkCraftWin
  );
  const silhouetteStreak = calculateStreak(
    "kisekidle-silhouette",
    SILHOUETTES,
    SILHOUETTES_MAP,
    checkSilhWin
  );

  /**
   * Determines display status (text, CSS class, score string) for a mode based on guess count and win state.
   *
   * @param {Array} guesses - The player's guesses for this mode.
   * @param {number} maxGuesses - Maximum allowed guesses.
   * @param {boolean} hasWon - Whether the player won.
   * @returns {{ text: string, class: string, score: string }}
   */
  const getStatus = (guesses, maxGuesses, hasWon) => {
    if (guesses.length === 0)
      return { text: "Not Played", class: "status-unplayed", score: "" };
    if (hasWon)
      return {
        text: "Won! 🎉",
        class: "status-won",
        score: `${guesses.length}/${maxGuesses}`,
      };
    if (guesses.length >= maxGuesses)
      return {
        text: "Lost ❌",
        class: "status-lost",
        score: `X/${maxGuesses}`,
      };
    return {
      text: "In Progress ⏳",
      class: "status-playing",
      score: `${guesses.length}/${maxGuesses}`,
    };
  };

  const charStatus = getStatus(
    charData.guesses,
    10,
    charData.guesses.some((g) => checkCharWin(g, targetChar))
  );
  const quoteStatus = getStatus(
    quoteData.guesses,
    6,
    quoteData.guesses.some((g) => checkQuoteWin(g, targetQuote))
  );
  const musicStatus = getStatus(
    musicData.guesses,
    6,
    musicData.guesses.some((g) => checkMusicWin(g, targetMusic))
  );
  const locStatus = getStatus(
    locData.guesses,
    5,
    locData.guesses.some((g) => checkLocWin(g, targetLocation))
  );
  const triviaStatus = getStatus(
    triviaData.guesses,
    2,
    triviaData.guesses.some((g) => checkTriviaWin(g, targetTrivia))
  );
  const craftStatus = getStatus(
    craftData.guesses,
    6,
    craftData.guesses.some((g) => checkCraftWin(g, targetCraft))
  );
  const silhouetteStatus = getStatus(
    silhouetteData.guesses,
    5,
    silhouetteData.guesses.some((g) => checkSilhWin(g, targetSilhouette))
  );

  /** Builds an emoji share string (🟩🟥⬛) representing guess outcomes for a mode. */
  const generateEmojiString = (guesses, maxGuesses, checkWinFn, target) => {
    if (guesses.length === 0) return "⬛".repeat(maxGuesses);
    let str = guesses
      .map((g) => (checkWinFn(g, target) ? "🟩" : "🟥"))
      .join("");
    if (guesses.length < maxGuesses) {
      str += "⬛".repeat(maxGuesses - guesses.length);
    }
    return str;
  };

  /** Copies shareable results text (emoji or text format) to clipboard. */
  const handleShare = (format) => {
    const shareHeader = isArchive
      ? `Kisekidle (Archive: ${targetDateStr})`
      : `Kisekidle (${targetDateStr})`;

    let textToShare = `${shareHeader}\n`;

    const addShareLine = (
      icon,
      name,
      data,
      target,
      maxGuesses,
      winCheckFn,
      streak
    ) => {
      if (data.guesses.length === 0) return;

      if (format === "emoji") {
        const emojis = generateEmojiString(
          data.guesses,
          maxGuesses,
          winCheckFn,
          target
        );
        const streakStr = streak > 0 ? ` (Streak: ${streak} 🔥)` : "";
        textToShare += `${icon} ${emojis} ${streakStr}\n`;
      } else {
        const hasWon = data.guesses.some((g) => winCheckFn(g, target));
        const scoreStr = hasWon
          ? `${data.guesses.length}/${maxGuesses}`
          : data.guesses.length >= maxGuesses
            ? `X/${maxGuesses}`
            : "In Progress";
        const streakStr = streak > 0 ? ` (Streak: ${streak} 🔥)` : "";
        textToShare += `${icon} ${name}: ${scoreStr}${streakStr}\n`;
      }
    };

    addShareLine(
      "🎭",
      "Character",
      charData,
      targetChar,
      10,
      checkCharWin,
      charStreak
    );
    addShareLine(
      "💬",
      "Quote",
      quoteData,
      targetQuote,
      6,
      checkQuoteWin,
      quoteStreak
    );
    addShareLine(
      "🎵",
      "Music",
      musicData,
      targetMusic,
      6,
      checkMusicWin,
      musicStreak
    );
    addShareLine(
      "📍",
      "Location",
      locData,
      targetLocation,
      5,
      checkLocWin,
      locStreak
    );

    if (version === "new") {
      addShareLine(
        "🧠",
        "Trivia",
        triviaData,
        targetTrivia,
        2,
        checkTriviaWin,
        triviaStreak
      );
      addShareLine(
        "⚔️",
        "Crafts",
        craftData,
        targetCraft,
        6,
        checkCraftWin,
        craftStreak
      );
      addShareLine(
        "👥",
        "Silhouette",
        silhouetteData,
        targetSilhouette,
        5,
        checkSilhWin,
        silhouetteStreak
      );
    }

    textToShare += `\nhttps://kisekidle.app/`;

    navigator.clipboard.writeText(textToShare);
    setIsCopied(format);
    setTimeout(() => setIsCopied(null), 2000);
  };

  /** Animated card showing a mode's icon, name, win/loss status, score, and streak. */
  const ResultCard = ({ icon, name, status, streak, index }) => (
    <motion.div
      className="result-card"
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ 
        type: "spring", 
        stiffness: 300, 
        damping: 25,
        delay: index * 0.1 
      }}
      whileHover={{ y: -5, transition: { duration: 0.2 } }}
    >
      <div className="result-icon">{icon}</div>
      <div className="result-name">{name}</div>
      <div className={`result-status ${status.class}`}>{status.text}</div>
      <div className="result-score">{status.score || "-"}</div>
      {streak > 0 && (
        <div className="result-streak">
          🔥 Streak: {streak}
        </div>
      )}
    </motion.div>
  );

  return (
    <div className="mode-container">
      <div className="header-container">
        <h2 className="mode-title">
          {isArchive
            ? `📅 Archive Results: ${targetDateStr}`
            : "📊 Today's Results"}
        </h2>
      </div>

      <motion.div 
        className="results-grid"
        initial="hidden"
        animate="visible"
        variants={{
          visible: { transition: { staggerChildren: 0.1 } }
        }}
      >
        <ResultCard
          icon="🎭"
          name="Character"
          status={charStatus}
          streak={charStreak}
          index={0}
        />
        <ResultCard
          icon="💬"
          name="Quote"
          status={quoteStatus}
          streak={quoteStreak}
          index={1}
        />
        <ResultCard
          icon="🎵"
          name="Music"
          status={musicStatus}
          streak={musicStreak}
          index={2}
        />
        <ResultCard
          icon="📍"
          name="Location"
          status={locStatus}
          streak={locStreak}
          index={3}
        />

        {version === "new" && (
          <>
            <ResultCard
              icon="🧠"
              name="Trivia"
              status={triviaStatus}
              streak={triviaStreak}
              index={4}
            />
            <ResultCard
              icon="⚔️"
              name="Crafts"
              status={craftStatus}
              streak={craftStreak}
              index={5}
            />
            <ResultCard
              icon="👥"
              name="Silhouette"
              status={silhouetteStatus}
              streak={silhouetteStreak}
              index={6}
            />
          </>
        )}
      </motion.div>

      {((charData.guesses?.length || 0) > 0 ||
        (quoteData.guesses?.length || 0) > 0 ||
        (musicData.guesses?.length || 0) > 0 ||
        (locData.guesses?.length || 0) > 0 ||
        (triviaData.guesses?.length || 0) > 0 ||
        (craftData.guesses?.length || 0) > 0 ||
        (silhouetteData.guesses?.length || 0) > 0) && (
          <div className="share-buttons-container">
            <button
              className="share-all-btn global-stats-trigger premium-hover"
              onClick={() => setIsModalOpen(true)}
            >
              📊 View Global Community Stats
            </button>

            <button
              className="share-all-btn premium-hover"
              onClick={() => handleShare("emoji")}
            >
              {isCopied === "emoji"
                ? "📋 Copied to Clipboard!"
                : "📤 Share as Emojis"}
            </button>

            <button className="share-all-btn premium-hover" onClick={() => handleShare("text")}>
              {isCopied === "text"
                ? "📋 Copied to Clipboard!"
                : "📤 Share as Text"}
            </button>
          </div>
        )}
    </div>
  );
}

export default ResultsMode;
